import sortBy from 'lodash/sortBy.js';

import { handleResponseError } from '@/Helpers.js';
import ActivityTemplate from '@/models/ActivityTemplate.js';
import CourseTemplateMenuItem from '@/models/CourseTemplateMenuItem.js';
import Model from '@/models/Model.js';
import SectionTemplate from '@/models/SectionTemplate.js';

export default class CourseTemplate extends Model {
    static entity = 'course_templates';

    static modelName = 'CourseTemplate';

    static fields() {
        return {
            id: this.attr(null),
            course_type_id: this.attr(null),
            course_settings_template_id: this.attr(null),
            grading_scheme_template_id: this.attr(null),
            title: this.attr(null),
            description: this.attr(null),
            pseudo_start_date: this.attr(null),
            is_active: this.boolean(null),
            published_at: this.attr(null),
            has_gradable_activity: this.attr(null),
            items: this.attr(null),
            tags: this.attr([]),

            ...super.fields(),

            course_codes: this.morphToMany(
                'course-codes',
                'course_codeables',
                'course_code_id',
                'course_codeable_id',
                'course_codeable_type'
            ),

            course_settings_template: this.belongsTo('course_settings_templates', 'course_settings_template_id'),
            activity_templates: this.hasMany('activity_templates', 'course_template_id'),
            section_templates: this.hasMany('section_templates', 'course_template_id'),
            groups: this.belongsToMany('groups', 'course_template_group', 'course_template_id', 'group_id'),
        };
    }

    get weeks() {
        return sortBy(
            this.section_templates.filter(sectionTemplate =>
                ['week', 'break'].includes(sectionTemplate.section_settings_template.type)
            ),
            'section_settings_template.week_number'
        );
    }

    get weeks_without_breaks() {
        return sortBy(
            this.section_templates.filter(sectionTemplate => sectionTemplate.section_settings_template.type === 'week'),
            'section_settings_template.week_number'
        );
    }

    get has_flexible_attendance() {
        return this.course_settings_template.has_flexible_attendance;
    }

    get show_student_enrollments() {
        return this.course_settings.show_student_enrollments;
    }

    get permit_extra_credit_overflow() {
        return this.course_settings.permit_extra_credit_overflow;
    }

    get scheduled_time() {
        const totals = {
            total_minutes: 0,
            total_break_minutes: 0,
            hours: 0,
            minutes: 0,
            break_hours: 0,
            break_minutes: 0,
        };

        this.section_templates.forEach(sectionTemplate =>
            sectionTemplate.time_period_templates.forEach(timePeriodTemplate => {
                totals.total_minutes += timePeriodTemplate.scheduled_time.total_minutes;
                totals.total_break_minutes += timePeriodTemplate.scheduled_time.break_minutes;
            })
        );

        totals.hours = Math.floor(totals.total_minutes / 60);
        totals.minutes = totals.total_minutes - totals.hours * 60;

        totals.break_hours = Math.floor(totals.total_break_minutes / 60);
        totals.break_minutes = totals.total_break_minutes - totals.break_hours * 60;

        return totals;
    }

    get computedMenu() {
        const rootItems = this.sectionAndActivityTemplates(true);

        return {
            primary: [],
            curriculum: {
                misc_pre: this.constructMenuItems(rootItems),
                weeks: {
                    past: [],
                    present: [],
                    future: [],
                },
                misc_post: [],
            },
            meta: [],
        };
    }

    menu() {
        const rootItems = this.sectionAndActivityTemplates(true);

        const menu = {
            primary: [],
            curriculum: {
                misc_pre: [],
                weeks: {
                    past: [],
                    present: [],
                    future: [],
                },
                misc_post: [],
            },
            meta: [],
        };

        // Loop through the rootItems and:
        // 1. Collect week/break items for the Upcoming Weeks group (templates are not launched)
        // 2. If the item is not a week or break and weeks have not already been added, add it to misc_pre
        // 3. If the item is not a week or break and weeks have already been added, add it to misc_post

        const weekAndBreakItems = [];
        let encounteredWeekOrBreak = false;

        rootItems.forEach((menuItem, index) => {
            if (this.isWeekOrBreakSection(menuItem)) {
                weekAndBreakItems.push({ menuItem, index });
                encounteredWeekOrBreak = true;
            } else if (encounteredWeekOrBreak) {
                menu.curriculum.misc_post.push(this.constructMenuItem(menuItem, index));
            } else {
                menu.curriculum.misc_pre.push(this.constructMenuItem(menuItem, index));
            }
        });

        weekAndBreakItems.forEach(({ menuItem, index }) => {
            menu.curriculum.weeks.future.push(this.constructMenuItem(menuItem, index));
        });

        return menu;
    }

    isWeekOrBreakSection(menuItem) {
        if (menuItem?.is_week || menuItem?.is_break) {
            return true;
        }

        const sectionType = menuItem?.section_settings_template?.type;

        return sectionType === 'week' || sectionType === 'break';
    }

    constructMenuItem(item, index, parentId = null) {
        const isSectionTemplate = item instanceof SectionTemplate;

        const buildNestedItems = buildNestedItem => {
            if (!(buildNestedItem instanceof SectionTemplate)) return [];

            return this.sectionAndActivityTemplates()
                .filter(
                    nestedItem =>
                        nestedItem.parent_id === buildNestedItem.id ||
                        (nestedItem instanceof ActivityTemplate &&
                            nestedItem.section_template_id === buildNestedItem.id)
                )
                .map((nestedItem, nestedIndex) => {
                    return this.constructMenuItem(nestedItem, nestedIndex, buildNestedItem.id);
                });
        };

        const nestedItems = isSectionTemplate ? buildNestedItems(item) : [];

        return new CourseTemplateMenuItem(item.menuItem(parentId, index, nestedItems));
    }

    constructMenuItems(items, parentId = null) {
        return items.map((item, index) => this.constructMenuItem(item, index, parentId));
    }

    sectionAndActivityTemplates(topLevel = false) {
        const sectionTemplates = SectionTemplate.query()
            .with('section_settings_template')
            .where('course_template_id', this.id)
            .where(sectionTemplate => (topLevel ? !sectionTemplate.parent_id : true))
            .get();

        const activityTemplates = ActivityTemplate.query()
            .where('course_template_id', this.id)
            .where(activityTemplate => (topLevel ? !activityTemplate.section_template_id : true))
            .get();

        return sortBy(sectionTemplates.concat(activityTemplates), 'order');
    }

    static async getCourseCodeTemplates(courseCodeId) {
        const endpoint = `/api/course-templates?course_code=${courseCodeId}`;
        try {
            const { response } = await this.api().get(endpoint);
            return response;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }
}
